import { html, css, nothing } from 'lit';
import { STEPS, REQUEST_TYPE_LABELS } from './states.js';

/**
 * The order-lifecycle pieces of the handoff (screens 4-7): the reference chip, the summary rows,
 * the three-step stepper and the delivered-documents list. Pure templates over plain data so the
 * app's screens stay readable and the stub and the BFF feed them the same shapes.
 */

export const lifecycleStyles = css`
  .order-ref {
    display: inline-flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 700;
    color: var(--pbg-navy); background: var(--pbg-blue-tint); padding: 9px 14px;
    border-radius: var(--pbg-radius-sm); font-variant-numeric: tabular-nums;
  }

  /* processing steps */
  .steps { display: flex; flex-direction: column; gap: 0; margin: 4px 0; }
  .step { display: flex; gap: 13px; padding: 2px 0; }
  .step .rail { display: flex; flex-direction: column; align-items: center; flex: 0 0 auto; }
  .step .node {
    width: 24px; height: 24px; border-radius: 50%; border: 2px solid var(--pbg-gray-200);
    background: var(--pbg-surface); display: flex; align-items: center; justify-content: center;
    font-size: 12px; color: var(--pbg-gray-400); font-weight: 800; transition: all .3s;
  }
  .step .line { width: 2px; flex: 1; min-height: 22px; background: var(--pbg-gray-200); transition: background .3s; }
  .step:last-child .line { display: none; }
  .step .scontent { padding-bottom: 18px; }
  .step .stitle { font-size: 13.5px; font-weight: 600; color: var(--pbg-gray-400); transition: color .3s; }
  .step .sdesc { font-size: 12px; color: var(--pbg-gray-400); margin-top: 2px; }
  .step.done .node { background: var(--pbg-success); border-color: var(--pbg-success); color: #fff; }
  .step.done .line { background: var(--pbg-success); }
  .step.done .stitle { color: var(--pbg-ink); }
  .step.active .node { background: var(--pbg-surface); border-color: var(--pbg-blue); color: var(--pbg-blue); }
  .step.active .node .mspin {
    width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(31, 95, 168, .3);
    border-top-color: var(--pbg-blue); animation: spin .7s linear infinite;
  }
  .step.active .stitle { color: var(--pbg-navy); }
  .step.active .sdesc { color: var(--pbg-gray-700); }
  @media (prefers-reduced-motion: reduce) { .step.active .node .mspin { animation-duration: 2s; } }

  /* delivered docs list */
  .delivered { display: flex; flex-direction: column; gap: 8px; }
  .dl-row {
    display: flex; align-items: center; gap: 12px; padding: 11px 13px;
    border: 1px solid var(--pbg-gray-200); border-radius: var(--pbg-radius-sm); background: var(--pbg-surface);
  }
  .dl-check {
    width: 22px; height: 22px; border-radius: 50%; background: var(--pbg-success); color: #fff;
    display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; flex: 0 0 auto;
  }
  .dl-row .dl-name { display: block; font-size: 13px; font-weight: 600; color: var(--pbg-ink); overflow-wrap: anywhere; }
  .dl-row .dl-sub { display: block; font-size: 11.5px; color: var(--pbg-gray-700); margin-top: 1px; }
  .dl-row .dl-spacer { flex: 1; }
  .dl-row .dl-time { font-size: 11px; color: var(--pbg-gray-400); font-variant-numeric: tabular-nums; white-space: nowrap; }
  @media (max-width: 480px) { .dl-row .dl-time { display: none; } }

  /* phase 2 chip */
  .phase2 {
    font-size: 9.5px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase;
    color: var(--pbg-accent-gold); background: #f6efdc; border: 1px solid #e8d9b0;
    padding: 2px 7px; border-radius: var(--pbg-radius-pill); margin-left: 4px; white-space: nowrap;
  }
`;

/** Screen 5's stepper. `step` is the active index; everything before it is done. */
export function stepper(step) {
  const active = Number.isInteger(step) ? Math.max(0, Math.min(step, STEPS.length - 1)) : 1;
  return html`
    <div class="steps" aria-live="polite" aria-label="Order progress">
      ${STEPS.map((s, i) => {
        const cls = i < active ? 'done' : i === active ? 'active' : '';
        return html`
          <div class="step ${cls}" data-step=${i} aria-current=${i === active ? 'step' : nothing}>
            <div class="rail">
              <div class="node" aria-hidden="true">
                ${i < active ? '✓' : i === active ? html`<span class="mspin"></span>` : i + 1}
              </div>
              <div class="line"></div>
            </div>
            <div class="scontent">
              <div class="stitle">${s.title}</div>
              <div class="sdesc">${s.desc}</div>
            </div>
          </div>`;
      })}
    </div>`;
}

/** Screens 6 and 7's delivered list. `docs` is [{ name, target, deliveredUtc }]. */
export function deliveredList(docs) {
  return html`
    <div class="delivered">
      ${(docs ?? []).map((d) => html`
        <div class="dl-row" data-delivered=${d.id ?? nothing}>
          <span class="dl-check" aria-hidden="true">✓</span>
          <span>
            <span class="dl-name">${d.name}</span>
            <span class="dl-sub">Delivered to eFolder${d.target ? ` · ${d.target}` : ''}</span>
          </span>
          <span class="dl-spacer"></span>
          ${d.deliveredUtc ? html`<span class="dl-time">${formatTime(d.deliveredUtc)}</span>` : nothing}
        </div>`)}
    </div>`;
}

/** Screen 4's summary rows: what was ordered, in the officer's words. */
export function orderSummaryRows({ loanNumber, order, documents }) {
  return html`
    <div class="frow"><span class="k">Loan</span><span class="v mono">${loanNumber || '—'}</span></div>
    <div class="frow"><span class="k">Request type</span><span class="v">${REQUEST_TYPE_LABELS[order?.requestType] ?? '—'}</span></div>
    ${order?.rush ? html`<div class="frow"><span class="k">Handling</span><span class="v">Rush</span></div>` : nothing}
    <hr class="divider" />
    ${(documents ?? []).map((d) => html`
      <div class="frow"><span class="k">${d.name}</span><span class="v">${d.type ?? d.target ?? 'eFolder'}</span></div>`)}`;
}

export function formatTime(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
